import React from "react";
import { Search, X, Filter } from "lucide-react";
import {
  StaffDepartment,
  StaffDepartmentLabels,
  EmployeeStatus,
  EmployeeStatusLabels,
  EmploymentType,
  EmploymentTypeLabels,
  StaffFilters,
} from "../../types/staff.types";

interface ClinicOption {
  id: string;
  name: string;
}

interface StaffFiltersBarProps {
  filters: StaffFilters;
  clinics?: ClinicOption[];
  onFilterChange: (filters: StaffFilters) => void;
  onReset: () => void;
}

export const StaffFiltersBar: React.FC<StaffFiltersBarProps> = ({
  filters,
  clinics,
  onFilterChange,
  onReset,
}) => {
  const hasActiveFilters = Boolean(
    filters.searchText ||
    filters.department ||
    filters.employeeStatus ||
    filters.employmentType ||
    filters.clinicId
  );

  return (
    <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm mb-4 space-y-3">
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        {/* Search input */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filters.searchText || ""}
            onChange={(e) =>
              onFilterChange({ ...filters, searchText: e.target.value })
            }
            placeholder="Search by name, code, mobile, designation, department..."
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all text-gray-800 placeholder-gray-400"
          />
          {filters.searchText && (
            <button
              onClick={() => onFilterChange({ ...filters, searchText: "" })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Dropdown Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Clinic Filter (for Admin) */}
          {clinics && clinics.length > 0 && (
            <select
              value={filters.clinicId || ""}
              onChange={(e) =>
                onFilterChange({ ...filters, clinicId: e.target.value })
              }
              className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all cursor-pointer max-w-[200px] truncate"
            >
              <option value="">All Clinics</option>
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          )}

          {/* Department Filter */}
          <select
            value={filters.department || ""}
            onChange={(e) =>
              onFilterChange({ ...filters, department: e.target.value })
            }
            className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all cursor-pointer"
          >
            <option value="">All Departments</option>
            {Object.values(StaffDepartment).map((dept) => (
              <option key={dept} value={dept}>
                {StaffDepartmentLabels[dept]}
              </option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filters.employeeStatus || ""}
            onChange={(e) =>
              onFilterChange({ ...filters, employeeStatus: e.target.value })
            }
            className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all cursor-pointer"
          >
            <option value="">All Statuses</option>
            {Object.values(EmployeeStatus).map((status) => (
              <option key={status} value={status}>
                {EmployeeStatusLabels[status]}
              </option>
            ))}
          </select>

          {/* Employment Type Filter */}
          <select
            value={filters.employmentType || ""}
            onChange={(e) =>
              onFilterChange({ ...filters, employmentType: e.target.value })
            }
            className="px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:bg-white transition-all cursor-pointer"
          >
            <option value="">All Employment Types</option>
            {Object.values(EmploymentType).map((type) => (
              <option key={type} value={type}>
                {EmploymentTypeLabels[type]}
              </option>
            ))}
          </select>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={onReset}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-100 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
